<template>
  <v-container>
    <ValidationObserver ref="objects" v-slot="{ invalid, dirty }">
      <v-form>
        <v-row>
          <v-col cols="12" md="6">
            <ValidationProvider name="First Name" rules="required|min:2|max:60">
              <v-text-field
                autocomplete="off"
                v-model="familyMemberLocal.first_name"
                :disabled="loading"
                label="First Name"
                ref="first_name"
                :error-messages="errors"
                slot-scope="{ errors }"
              ></v-text-field>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="Middle Name" rules="min:2|max:60">
              <v-text-field
                autocomplete="off"
                v-model="familyMemberLocal.middle_name"
                :disabled="loading"
                label="Middle Name"
                :error-messages="errors"
                slot-scope="{ errors }"
              ></v-text-field>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="Surname" rules="required|min:2|max:60">
              <v-text-field
                autocomplete="off"
                v-model="familyMemberLocal.surname"
                :disabled="loading"
                label="Surname"
                :error-messages="errors"
                slot-scope="{ errors }"
              ></v-text-field>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="DOB Date" rules="required|date">
              <DateInput
                v-model="familyMemberLocal.dob"
                label="DOB Date"
                :disabled="loading"
                :error-messages="errors"
                slot-scope="{ errors }"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="Nationality">
              <v-select
                v-model="familyMemberLocal.country_id"
                :disabled="loading"
                :items="lists.countries"
                :menu-props="{ maxHeight: '400' }"
                item-text="title"
                item-value="id"
                :error-messages="errors"
                slot-scope="{ errors }"
                label="Nationality"
                clearable
              ></v-select>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="Relationship" rules="required">
              <v-select
                v-model="familyMemberLocal.family_member_relationships_id"
                :disabled="loading"
                :items="lists.relationships"
                :menu-props="{ maxHeight: '400' }"
                item-text="title"
                item-value="id"
                :error-messages="errors"
                slot-scope="{ errors }"
                label="Relationship"
                clearable
              ></v-select>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field autocomplete="off" v-model="age" disabled label="Age" readonly></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="Notes" rules="required">
              <v-select
                v-model="familyMemberLocal.family_member_statuses_id"
                :disabled="loading"
                :items="lists.status"
                :menu-props="{ maxHeight: '400' }"
                item-text="title"
                item-value="id"
                :error-messages="errors"
                slot-scope="{ errors }"
                label="Status"
                clearable
              ></v-select>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="WWC#" rules="min:2">
              <v-text-field
                autocomplete="off"
                v-model="familyMemberLocal.card_number"
                :disabled="loading"
                label="WWC#"
                :error-messages="errors"
                slot-scope="{ errors }"
              ></v-text-field>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="WWC Expire" rules="date">
              <DateInput
                v-model="familyMemberLocal.card_expire"
                label="WWC Expire"
                :disabled="loading"
                :error-messages="errors"
                slot-scope="{ errors }"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="6">
            <ValidationProvider name="WWC Status">
              <v-select
                v-model="familyMemberLocal.card_statuses_id"
                :disabled="loading"
                :items="lists.card_statuses"
                :menu-props="{ maxHeight: '400' }"
                item-text="title"
                item-value="id"
                :error-messages="errors"
                slot-scope="{ errors }"
                label="WWC Status"
                clearable
              ></v-select>
            </ValidationProvider>
          </v-col>
          <v-col cols="12">
            <ValidationProvider name="WWC Note">
              <v-textarea v-model="familyMemberLocal.card_note" label="WWC Note" rows="3" filled auto-grow></v-textarea>
            </ValidationProvider>
          </v-col>
        </v-row>
        <div :class="actionBarContainerClasses">
          <v-btn @click="saveAction" :disabled="loading || invalid || !dirty" class="primary">Save</v-btn>
        </div>
      </v-form>
    </ValidationObserver>
    <ConfirmDialog :show="showNavigateAwayConfirmDialog" @confirm-dialog-clicked="confirmNavigate" />
  </v-container>
</template>

<script>
import { differenceInYears } from 'date-fns';
import loadDash from 'lodash';
import { extend, ValidationProvider, ValidationObserver } from 'vee-validate';
import { required, min, max } from 'vee-validate/dist/rules';
import { date } from '@/utils/helpers';
import { mapGetters } from 'vuex';
import store from '@/store/index';
import DateInput from '@/views/DateInput';
import ConfirmDialog from '@/views/dialogs/ConfirmDialog';

extend('required', required);
extend('min', min);
extend('max', max);
extend('date', date);

export default {
  name: 'FamilyMemberDetailForm',
  beforeRouteLeave(to, from, next) {
    if (this.isDirty()) {
      this.next = next;
      this.showNavigateAwayConfirmDialog = true;
    } else {
      next();
    }
  },
  components: {
    ConfirmDialog,
    DateInput,
    ValidationProvider,
    ValidationObserver
  },
  computed: {
    /**
     * The mapGetters helper simply maps store getters to local computed properties:
     */
    ...mapGetters('family', ['loading', 'familyMember', 'error', 'success', 'meta']),
    ...mapGetters('ui', ['mainMenuOpen']),
    /*
     * We retrive the meta data for each requests. This allows us to update the lists
     * if they have changed in the application.
     */
    lists: function () {
      if (this.meta && this.meta.lists) {
        return this.meta.lists;
      }
      return {
        status: [],
        relationships: [],
        card_statuses: [],
        countries: []
      };
    },

    // Add age as a computed property
    age() {
      if (this.familyMember && this.familyMember.dob) {
        return this.calculateAge(this.familyMember.dob);
      }
      return 0;
    },
    /**
     * To work with v-model and vuex, we only want to keep
     * a local copy until the user is ready to save.
     */
    familyMemberLocal: function () {
      const localFamily = loadDash.cloneDeep(this.familyMember);
      this.focusFirstField();
      return localFamily;
    },
    actionBarContainerClasses() {
      if (this.popup) {
        return {};
      } else {
        return {
          'fixed-action-bar': true,
          'fixed-action-bar--open': this.mainMenuOpen
        };
      }
    }
  },
  methods: {
    /**
     * Set the focus on the first field if possible
     */
    focusFirstField() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.$refs.first_name.focus();
        }, 100);
      });
    },
    /**
     * Save method for the form
     */
    async saveAction() {
      const result = await this.$refs.objects.validate();
      if (result) {
        if (this.isNewRecord()) {
          this.familyMemberLocal.organisation_id = this.$route.query.organisation_id || null;
        }
        await store.dispatch('family/save', {
          id: this.familyMemberLocal.id,
          familyMember: this.familyMemberLocal
        });
        this.resetValidation();
      }
    },
    /**
     * Check if this a new record
     */
    isNewRecord() {
      return this.$route.params.id == 'new' ? true : false;
    },
    /**
     * Reset the validation, this will make sure the dirty flags get set to
     * false after save action.
     */
    resetValidation() {
      requestAnimationFrame(() => {
        this.$refs.objects.reset();
      });
    },
    /**
     * Check if the form has changed after loading
     */
    isDirty() {
      return this.$refs.objects._data.flags.dirty;
    },
    /**
     * Calculate age from DOB
     */
    calculateAge(dobValue) {
      if (!dobValue) {
        return 0;
      }
      const dob = new Date(Date.parse(dobValue + ' 00:00:00'));
      return differenceInYears(new Date(), dob);
    },
    confirmNavigate: function (navigateAway) {
      this.showNavigateAwayConfirmDialog = false;
      if (navigateAway) {
        this.next();
      }
    }
  },
  mounted() {
    if (!this.isNewRecord()) {
      store.dispatch('family/get', this.$route.params.id);
    }
  },
  data: () => ({
    showNavigateAwayConfirmDialog: false,
    organisation_id: null
  })
};
</script>
